import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FiSave,
  FiX,
  FiMapPin,
  FiPhone,
  FiNavigation,
  FiTrash2,
  FiAlertCircle,
  FiGlobe,
  FiMap
} from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BaseUrl from '../../../Url/BaseUrl';

const EditBusStand = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { busStandCode } = useParams();

  const [formData, setFormData] = useState({
    busStandCode: '',
    busStandName: '',
    hindi: '',
    stateCode: '',
    districtCode: '',
    cityVillageCode: '',
    latitude: 0.0,
    longitude: 0.0,
    elevation: '',
    status: false,
    busStandContactNumber: { dialCode: '91', number: '' }
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cityVillages, setCityVillages] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCityVillages, setLoadingCityVillages] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ✅ Guard flag — prevents dropdown effects from resetting pre-filled codes during load[cite: 2]
  const [isPrefilling, setIsPrefilling] = useState(true);

  const BUS_STANDS_API = `${BaseUrl.baseurl}BusStands/${busStandCode}`;

  // 1. Fetch states on mount[cite: 2]
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await axios.get(`${BaseUrl.baseurl}States`, {
          headers: { accept: 'text/plain' }
        });
        setStates(response.data || []);
      } catch (err) {
        console.error('Error fetching states:', err);
        toast.error(t('failedToLoadStates'));
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, [t]);

  // 2. Fetch districts when stateCode changes[cite: 2]
  useEffect(() => {
    if (formData.stateCode) {
      const fetchDistricts = async () => {
        setLoadingDistricts(true);
        try {
          const response = await axios.get(
            `${BaseUrl.baseurl}Districts/GetDistrictsByStateId?StateCode=${formData.stateCode}`,
            { headers: { accept: 'text/plain' } }
          );
          setDistricts(response.data || []);
        } catch (err) {
          console.error('Error fetching districts:', err);
          toast.error(t('failedToLoadDistricts'));
        } finally {
          setLoadingDistricts(false);
        }
      };
      fetchDistricts();
    } else {
      setDistricts([]);
      if (!isPrefilling) {
        setFormData(prev => ({ ...prev, districtCode: '', cityVillageCode: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.stateCode]);

  // 3. Fetch city villages when districtCode changes[cite: 2]
  useEffect(() => {
    if (formData.districtCode) {
      const fetchCityVillages = async () => {
        setLoadingCityVillages(true);
        try {
          const response = await axios.get(
            `${BaseUrl.baseurl}CityVillages/GetCityVillagesByDistrictCode?DistrictCode=${formData.districtCode}`,
            { headers: { accept: 'text/plain' } }
          );
          setCityVillages(response.data || []);
        } catch (err) {
          console.error('Error fetching city villages:', err);
          toast.error(t('failedToLoadCityVillages'));
        } finally {
          setLoadingCityVillages(false);
        }
      };
      fetchCityVillages();
    } else {
      setCityVillages([]);
      if (!isPrefilling) {
        setFormData(prev => ({ ...prev, cityVillageCode: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.districtCode]);

  // 4. Load data from Router state or API endpoint, extracting state, district, & cityVillage[cite: 2]
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setIsPrefilling(true);
      try {
        let busStandData = location.state?.busStandPreview;

        if (!busStandData) {
          const busStandResponse = await axios.get(BUS_STANDS_API, {
            headers: { accept: 'text/plain' }
          });
          busStandData = busStandResponse.data;
        }

        // Deep extraction from nested object response
        const cityVillage = busStandData.cityVillage || {};
        const district = cityVillage.district || busStandData.district || {};
        const zone = district.zone || busStandData.zone || {};
        const state = zone.state || district.state || busStandData.state || {};

        const extractedStateCode =
          state.stateCode ||
          busStandData.stateCode ||
          '';

        const extractedDistrictCode =
          district.districtCode ||
          cityVillage.districtCode ||
          busStandData.districtCode ||
          '';

        const extractedCityVillageCode =
          cityVillage.cityVillageCode ||
          busStandData.cityVillageCode ||
          '';

        let contactNumberObj = { dialCode: '91', number: '' };
        if (busStandData.busStandContactNumber) {
          const match = busStandData.busStandContactNumber.match(/^\+(\d+)(.+)$/);
          if (match) {
            contactNumberObj = { dialCode: match[1], number: match[2] };
          } else {
            contactNumberObj.number = busStandData.busStandContactNumber;
          }
        }

        setFormData({
          busStandCode: busStandData.busStandCode || '',
          busStandName: busStandData.busStandName || '',
          hindi: busStandData.hindi || '',
          stateCode: extractedStateCode,
          districtCode: extractedDistrictCode,
          cityVillageCode: extractedCityVillageCode,
          latitude: busStandData.latitude || 0.0,
          longitude: busStandData.longitude || 0.0,
          elevation: busStandData.elevation || '',
          status: busStandData.status || false,
          busStandContactNumber: contactNumberObj
        });
      } catch (error) {
        console.error('Error fetching bus stand data:', error);
        if (error.response?.status === 404) {
          toast.error(t('busStandNotFound', { busStandCode }));
        } else {
          toast.error(t('failedToLoadBusStandData'));
        }
      } finally {
        setLoading(false);
        setTimeout(() => setIsPrefilling(false), 300);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busStandCode]);

  const validateForm = () => {
    const errors = {};
    if (!formData.busStandCode.trim()) errors.busStandCode = t('busStandCodeRequired');
    if (!formData.busStandName.trim()) errors.busStandName = t('busStandNameRequired');
    if (!formData.stateCode) errors.stateCode = t('stateRequired');
    if (!formData.districtCode) errors.districtCode = t('districtRequired');
    if (!formData.cityVillageCode) errors.cityVillageCode = t('cityVillageCodeRequired');
    if (
      formData.busStandContactNumber.number &&
      !/^\d{10,15}$/.test(formData.busStandContactNumber.number)
    ) {
      errors.busStandContactNumber = t('invalidContactNumberFormat');
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]:
          type === 'checkbox'
            ? checked
            : type === 'number'
            ? value === ''
              ? 0
              : parseFloat(value)
            : value
      };
      if (name === 'stateCode') {
        updated.districtCode = '';
        updated.cityVillageCode = '';
      }
      if (name === 'districtCode') {
        updated.cityVillageCode = '';
      }
      return updated;
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleContactNumberChange = (field, value) => {
    if (field === 'dialCode') {
      setFormData(prev => ({
        ...prev,
        busStandContactNumber: { ...prev.busStandContactNumber, dialCode: value }
      }));
    } else if (field === 'number') {
      const numericValue = value.replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({
        ...prev,
        busStandContactNumber: { ...prev.busStandContactNumber, number: numericValue }
      }));
    }
    if (validationErrors.busStandContactNumber) {
      setValidationErrors(prev => ({ ...prev, busStandContactNumber: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error(t('fixValidationErrors'));
      return;
    }
    setIsSubmitting(true);
    setErrorMessage('');

    const payload = {
      busStandCode: formData.busStandCode.trim(),
      busStandName: formData.busStandName.trim(),
      hindi: formData.hindi?.trim() || null,
      cityVillageCode: formData.cityVillageCode,
      latitude: parseFloat(formData.latitude.toFixed(6)),
      longitude: parseFloat(formData.longitude.toFixed(6)),
      elevation: formData.elevation?.trim() || null,
      status: formData.status,
      busStandContactNumber: formData.busStandContactNumber.number
        ? `+${formData.busStandContactNumber.dialCode}${formData.busStandContactNumber.number}`
        : null
    };
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === '') delete payload[key];
    });

    try {
      await axios.put(BUS_STANDS_API, payload, {
        headers: { accept: 'text/plain', 'Content-Type': 'application/json' }
      });

      toast.success(
        t('busStandUpdatedSuccess', { busStandName: formData.busStandName }),
        {
          style: {
            background: '#ffffff',
            color: '#1f2937',
            border: '1px solid #e5e7eb'
          }
        }
      );

      setTimeout(() => {
        navigate('/bus-finder/bus-stops/manage');
      }, 2000);
    } catch (error) {
      console.error('Update failed:', error);
      let errorMsg = '';
      if (error.response) {
        if (error.response.status === 404) errorMsg = t('busStandNotFoundError');
        else if (error.response.status === 400) errorMsg = t('invalidData');
        else if (error.response.status === 409) errorMsg = t('busStandCodeConflict');
        else
          errorMsg = t('serverError', {
            status: error.response.status,
            statusText: error.response.data || error.response.statusText
          });
      } else if (error.request) {
        errorMsg = t('networkError');
      } else {
        errorMsg = t('unexpectedError');
      }
      toast.error(errorMsg);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('confirmDeleteBusStand'))) return;
    setDeleting(true);
    try {
      await axios.delete(BUS_STANDS_API, { headers: { accept: 'text/plain' } });
      toast.success(t('busStandDeleted'), {
        style: { background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb' }
      });
      setTimeout(() => navigate('/bus-finder/bus-stops/manage'), 1500);
    } catch (error) {
      console.error('Delete failed:', error);
      if (error.response?.status === 404) toast.error(t('busStandNotFoundDelete'));
      else toast.error(t('failedToDeleteBusStand'));
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('confirmCancelChanges')))
      navigate('/bus-finder/bus-stops/manage');
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: parseFloat(position.coords.latitude.toFixed(6)),
            longitude: parseFloat(position.coords.longitude.toFixed(6))
          }));
        },
        () => toast.error(t('unableToRetrieveLocation'))
      );
    } else {
      toast.error(t('geolocationNotSupported'));
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <ClipLoader size={60} color="#00BCD4" />
        <p>{t('loadingBusStandData')}</p>
      </div>
    );
  }

  return (
    <div className="form-container">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <div className="form-header">
        <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
        <h1>
          <FiMapPin /> {t('edit')} {t('busStandsManagement')}: {formData.busStandCode}
        </h1>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          <FiAlertCircle /> {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-section">
          <h3>{t('basicInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="busStandCode">{t('busStandCodeLabel')} *</label>
              <input
                type="text"
                id="busStandCode"
                name="busStandCode"
                value={formData.busStandCode}
                disabled
                className={validationErrors.busStandCode ? 'error' : ''}
                required
              />
              {validationErrors.busStandCode && (
                <span className="error-text">{validationErrors.busStandCode}</span>
              )}
              <div className="hint">{t('busStandCodeHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="busStandName">{t('busStandNameLabel')} *</label>
              <input
                type="text"
                id="busStandName"
                name="busStandName"
                value={formData.busStandName}
                onChange={handleChange}
                className={validationErrors.busStandName ? 'error' : ''}
                disabled={isSubmitting}
                required
              />
              {validationErrors.busStandName && (
                <span className="error-text">{validationErrors.busStandName}</span>
              )}
              <div className="hint">{t('busStandNameHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="hindi">
                {t('busStandNameHiLabel', 'Bus Stand Name (Hindi)')}
              </label>
              <input
                type="text"
                id="hindi"
                name="hindi"
                value={formData.hindi}
                onChange={handleChange}
                placeholder={t('busStandNameHiPlaceholder', 'e.g., सेंट्रल बस टर्मिनल')}
                disabled={isSubmitting}
              />
              <div className="hint">
                {t('busStandNameHiHint', 'Hindi name of the bus stand (optional)')}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="status">{t('statusLabel')}</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="status"
                    checked={formData.status}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">{t('active')}</span>
                </label>
              </div>
              <div className="hint">{t('activeHint')}</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('locationDetails')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="stateCode">
                <FiGlobe /> {t('state')} *
              </label>
              <select
                id="stateCode"
                name="stateCode"
                value={formData.stateCode}
                onChange={handleChange}
                disabled={loadingStates || isSubmitting}
                className={validationErrors.stateCode ? 'error' : ''}
                required
              >
                <option value="">{t('selectState')}</option>
                {states.map(state => (
                  <option key={state.stateCode} value={state.stateCode}>
                    {state.stateName}
                  </option>
                ))}
              </select>
              {loadingStates && <span className="loading-text">{t('loading')}</span>}
              {validationErrors.stateCode && (
                <span className="error-text">{validationErrors.stateCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="districtCode">
                <FiMap /> {t('district')} *
              </label>
              <select
                id="districtCode"
                name="districtCode"
                value={formData.districtCode}
                onChange={handleChange}
                disabled={loadingDistricts || isSubmitting || !formData.stateCode}
                className={validationErrors.districtCode ? 'error' : ''}
                required
              >
                <option value="">{t('selectDistrict')}</option>
                {districts.map(district => (
                  <option key={district.districtCode} value={district.districtCode}>
                    {district.districtName}
                  </option>
                ))}
              </select>
              {loadingDistricts && <span className="loading-text">{t('loading')}</span>}
              {validationErrors.districtCode && (
                <span className="error-text">{validationErrors.districtCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="cityVillageCode">{t('selectCityVillageLabel')} *</label>
              <select
                id="cityVillageCode"
                name="cityVillageCode"
                value={formData.cityVillageCode}
                onChange={handleChange}
                disabled={loadingCityVillages || isSubmitting || !formData.districtCode}
                className={validationErrors.cityVillageCode ? 'error' : ''}
                required
              >
                <option value="">{t('selectCityVillage')}</option>
                {cityVillages.map(cv => (
                  <option key={cv.cityVillageCode} value={cv.cityVillageCode}>
                    {cv.cityVillageName}
                  </option>
                ))}
              </select>
              {loadingCityVillages && <span className="loading-text">{t('loading')}</span>}
              {validationErrors.cityVillageCode && (
                <span className="error-text">{validationErrors.cityVillageCode}</span>
              )}
              <div className="hint">{t('searchAndSelectHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="latitude">
                {t('latitudeLabel')} *
                <button
                  type="button"
                  className="btn-location"
                  onClick={getCurrentLocation}
                  disabled={isSubmitting}
                >
                  <FiNavigation /> {t('getLocation')}
                </button>
              </label>
              <input
                type="number"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                step="0.000001"
                min="-90"
                max="90"
                className={validationErrors.latitude ? 'error' : ''}
                disabled={isSubmitting}
                required
              />
              {validationErrors.latitude && (
                <span className="error-text">{validationErrors.latitude}</span>
              )}
              <div className="hint">{t('latitudeHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="longitude">{t('longitudeLabel')} *</label>
              <input
                type="number"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                step="0.000001"
                min="-180"
                max="180"
                className={validationErrors.longitude ? 'error' : ''}
                disabled={isSubmitting}
                required
              />
              {validationErrors.longitude && (
                <span className="error-text">{validationErrors.longitude}</span>
              )}
              <div className="hint">{t('longitudeHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="elevation">{t('elevationLabel')}</label>
              <input
                type="text"
                id="elevation"
                name="elevation"
                value={formData.elevation}
                onChange={handleChange}
                placeholder={t('elevationPlaceholder')}
                disabled={isSubmitting}
              />
              <div className="hint">{t('elevationHint')}</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('contactInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="busStandContactNumber">
                <FiPhone /> {t('contactNumberLabel')}
              </label>
              <div className="phone-input-wrapper">
                <select
                  value={formData.busStandContactNumber.dialCode}
                  onChange={(e) => handleContactNumberChange('dialCode', e.target.value)}
                  className="country-code-select"
                  disabled={isSubmitting}
                >
                  <option value="91">🇮🇳 +91 (India)</option>
                  <option value="1">🇺🇸 +1 (USA/Canada)</option>
                  <option value="44">🇬🇧 +44 (UK)</option>
                  <option value="61">🇦🇺 +61 (Australia)</option>
                  <option value="971">🇦🇪 +971 (UAE)</option>
                  <option value="65">🇸🇬 +65 (Singapore)</option>
                  <option value="60">🇲🇾 +60 (Malaysia)</option>
                  <option value="64">🇳🇿 +64 (New Zealand)</option>
                  <option value="49">🇩🇪 +49 (Germany)</option>
                  <option value="33">🇫🇷 +33 (France)</option>
                  <option value="81">🇯🇵 +81 (Japan)</option>
                  <option value="86">🇨🇳 +86 (China)</option>
                  <option value="82">🇰🇷 +82 (South Korea)</option>
                  <option value="966">🇸🇦 +966 (Saudi Arabia)</option>
                  <option value="974">🇶🇦 +974 (Qatar)</option>
                  <option value="965">🇰🇼 +965 (Kuwait)</option>
                  <option value="968">🇴🇲 +968 (Oman)</option>
                  <option value="880">🇧🇩 +880 (Bangladesh)</option>
                  <option value="94">🇱🇰 +94 (Sri Lanka)</option>
                  <option value="977">🇳🇵 +977 (Nepal)</option>
                  <option value="92">🇵🇰 +92 (Pakistan)</option>
                  <option value="93">🇦🇫 +93 (Afghanistan)</option>
                </select>
                <input
                  type="tel"
                  id="busStandContactNumber"
                  value={formData.busStandContactNumber.number}
                  onChange={(e) => handleContactNumberChange('number', e.target.value)}
                  placeholder={t('contactNumberPlaceholder')}
                  className={validationErrors.busStandContactNumber ? 'error' : ''}
                  disabled={isSubmitting}
                  maxLength="15"
                />
              </div>
              {validationErrors.busStandContactNumber && (
                <span className="error-text">
                  {validationErrors.busStandContactNumber}
                </span>
              )}
              <div className="hint">
                {t('contactNumberHint', 'Format: Select country code and enter phone number')}
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <div className="form-actions-left">
            <button
              type="button"
              className="btn-danger"
              onClick={() => setDeleteConfirm(true)}
              disabled={isSubmitting || deleting}
            >
              {deleting ? (
                <>
                  <ClipLoader size={16} color="#fff" /> {t('deleting')}
                </>
              ) : (
                <>
                  <FiTrash2 /> {t('deleteBusStand')}
                </>
              )}
            </button>
          </div>
          <div className="form-actions-right">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting || deleting}
            >
              <FiX /> {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || deleting}
            >
              {isSubmitting ? (
                <>
                  <ClipLoader size={16} color="#fff" /> {t('updating')}
                </>
              ) : (
                <>
                  <FiSave /> {t('updateBusStand')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <FiAlertCircle size={24} color="#dc3545" />
              <h3>{t('confirmDelete')}</h3>
            </div>
            <div className="modal-content">
              <p>
                {t('confirmDeleteMessage', {
                  busStandName: formData.busStandName,
                  busStandCode: formData.busStandCode
                })}
              </p>
              <p className="warning-text">{t('deleteWarning')}</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirm(false)}
                disabled={deleting}
              >
                {t('cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? t('deleting') : t('deletePermanently')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .form-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        }
        .loading-container p {
          margin-top: 1rem;
          color: #666;
        }
        .form-header {
          margin-bottom: 2rem;
        }
        .btn-back {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          cursor: pointer;
          margin-bottom: 1rem;
          transition: all 0.2s;
        }
        .btn-back:hover {
          background: #e9ecef;
          border-color: #adb5bd;
          transform: translateX(-2px);
        }
        .form-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .form-section {
          margin-bottom: 2.5rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        .form-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f0f0f0;
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          position: relative;
        }
        .form-group label {
          font-weight: 500;
          color: #495057;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .loading-text {
          font-size: 0.85rem;
          color: #6c757d;
          font-style: italic;
        }
        .btn-location {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          transition: all 0.2s;
        }
        .btn-location:hover:not(:disabled) {
          background: #c8e6c9;
        }
        .btn-location:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .phone-input-wrapper {
          display: flex;
          gap: 0.5rem;
        }
        .country-code-select {
          width: 140px;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          cursor: pointer;
        }
        .country-code-select:focus {
          outline: none;
          border-color: #00BCD4;
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }
        .form-group input, .form-group select {
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          font-family: inherit;
        }
        .form-group input:focus, .form-group select:focus {
          outline: none;
          border-color: #00BCD4;
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }
        .form-group input.error, .form-group select.error {
          border-color: #dc3545;
          background: #fff5f5;
        }
        .form-group input:disabled, .form-group select:disabled {
          background: #e9ecef;
          cursor: not-allowed;
          opacity: 0.7;
        }
        .error-text {
          color: #dc3545;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }
        .hint {
          color: #6c757d;
          font-size: 0.85rem;
          margin-top: 0.25rem;
          font-style: italic;
        }
        .checkbox-group {
          margin-top: 0.5rem;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          user-select: none;
        }
        .checkbox-label input[type="checkbox"] {
          display: none;
        }
        .checkbox-custom {
          width: 20px;
          height: 20px;
          border: 2px solid #dee2e6;
          border-radius: 4px;
          position: relative;
          transition: all 0.2s;
        }
        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom {
          background: #00BCD4;
          border-color: #00BCD4;
        }
        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 0.8rem;
          font-weight: bold;
        }
        .checkbox-text {
          font-size: 1rem;
          color: #333;
        }
        .form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 2rem;
          margin-top: 2rem;
          border-top: 1px solid #dee2e6;
        }
        .form-actions-left, .form-actions-right {
          display: flex;
          gap: 1rem;
        }
        .btn-secondary, .btn-primary, .btn-danger {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }
        .btn-secondary {
          background: #6c757d;
          color: white;
        }
        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }
        .btn-primary {
          background: linear-gradient(135deg, #00BCD4 0%, #0097A7 100%);
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .btn-danger {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white;
        }
        .btn-danger:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .btn-secondary:disabled, .btn-primary:disabled, .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .alert {
          padding: 1rem 1.5rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: slideIn 0.3s ease;
        }
        @keyframes slideIn {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        .modal-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .modal-header h3 {
          margin: 0;
          color: #333;
        }
        .modal-content {
          margin-bottom: 2rem;
        }
        .modal-content p {
          color: #666;
          margin-bottom: 1rem;
        }
        .warning-text {
          color: #dc3545;
          font-weight: 500;
          font-size: 0.95rem;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
        }
        @media (max-width: 768px) {
          .form-container {
            padding: 1rem;
          }
          .form-section {
            padding: 1rem;
          }
          .form-grid {
            grid-template-columns: 1fr;
          }
          .phone-input-wrapper {
            flex-direction: column;
          }
          .country-code-select {
            width: 100%;
          }
          .form-actions {
            flex-direction: column;
            gap: 1rem;
          }
          .form-actions-left, .form-actions-right {
            width: 100%;
            justify-content: stretch;
          }
          .btn-secondary, .btn-primary, .btn-danger {
            width: 100%;
            justify-content: center;
          }
          .modal {
            padding: 1.5rem;
          }
          .modal-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default EditBusStand;